import React, {useContext, useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {withRouter, resolveRoute, useLocation} from '@computerrock/formation-router';
import {useTranslate} from '@computerrock/formation-i18n';
import {topicTypes} from '@swiss-ski/sski-entity-types';
import './TopNavigationBar.scss';
import routePaths from '../../routePaths';
import useOutsideClick from '../../utils/useOutsideClick';
import {CATEGORY_ID, CLEAR_SEARCH, DEFAULT_CATS_URL} from '../../utils/ftemConstants';
import {MobileDevice} from '../../index';
import * as categoryActionTypes from '../categoryActionTypes';
import PrintData from './PrintData';
import CategoriesContext from '../CategoriesContext';
import PhaseLoader from '../../dashboard/ui-elements/PhaseLoader';
import SearchNavigation from './SearchNavigation';

const TopNavigationBar = props => {
    const {history, categoryOptions, setIsLoading, resetCategories, sports, sportId, isLoading} = props;
    const {createTranslateShorthand} = useTranslate();
    const translateScreen = createTranslateShorthand('categories_screen');
    const {column, search} = useContext(CategoriesContext);
    const selectedColumn = column[0];
    const setSelectedColumn = column[1];
    const setIsSearched = search[1];
    const isSearch = search?.[0] && search[0] !== CLEAR_SEARCH ? search[0] : false;
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isPrintUrl, setIsPrintUrl] = useState(false);
    const [searchedTerm, setSearchedTerm] = useState('');
    const dropDownRef = useRef(null);
    const {isMobile} = useContext(MobileDevice);
    const availableCategories = (categoryOptions || []).filter(category => category.topicGroup
        && category.topicGroup !== 'none'
        && category.id !== (topicTypes.AGE_CATEGORY || '').toLowerCase()).filter(id => (
        id !== topicTypes.AGE_CATEGORY.toLowerCase()
    ));
    const groupedCategories = availableCategories.reduce((acc, category) => {
        acc[category.topicGroup] = [...(acc[category.topicGroup] ? acc[category.topicGroup] : []), category];
        return acc;
    }, {});

    const [selectedCategoryIds, setSelectedCategoryIds] = useState([]);
    const visibleCategories = selectedCategoryIds.filter(
        id => id !== topicTypes.AGE_CATEGORY.toLowerCase(),
    );
    const {location} = history ?? {};
    const searchParams = new URLSearchParams(location.search);
    const getSportId = searchParams.get('sportId');
    const getPrintTitle = () => sports.find(sport => sport.idNum == (sportId || getSportId))?.title ?? '';
    const [printTitle, setPrintTitle] = useState(null);
    const [prevQuery, setPrevQuery] = useState('');
    const excludeNotInObjectIds = (arr, objArr) => {
        return arr.filter(id => objArr.some(obj => obj.id === id));
    };

    const handleClickOutside = () => {
        const queryParams = new URLSearchParams(location.search);
        const categoryIds = queryParams.getAll(CATEGORY_ID);
        setSelectedCategoryIds(categoryIds);
        setIsMenuOpen(false);
        setSearchedTerm(!isSearch || isSearch === CLEAR_SEARCH ? '' : isSearch);
    };

    const handleClickBack = () => {
        const queryParams = new URLSearchParams(location.search);
        const getSportId = queryParams.get('sportId');
        const getSportName = sports.find(sport => sport.idNum == getSportId)?.id;
        queryParams.delete(CATEGORY_ID);
        resetCategories({});
        history.push({pathname: `/dashboard/${getSportName}`});
    };

    const handleSubmit = () => {
        const queryParams = new URLSearchParams(location.search);
        const newQueryParams = new URLSearchParams();
        selectedCategoryIds.forEach(categoryId => {
            newQueryParams.append(CATEGORY_ID, categoryId);
        });
        if (queryParams.get('locale')) newQueryParams.append('locale', queryParams.get('locale'));
        newQueryParams.set('sportId', sportId || getSportId);
        history.replace(resolveRoute(routePaths.CATEGORIES, {}, {search: newQueryParams.toString()}));
        setIsMenuOpen(false);
        setIsLoading(true);
    };

    const handleSearch = e => {
        setSearchedTerm(e.target.value);
    };

    const resetAll = () => {
        setSelectedCategoryIds([topicTypes.AGE_CATEGORY.toLowerCase()]);
    };

    const handleGroup = group => {
        if (!group || typeof group !== 'string') return;

        const groupIds = (availableCategories || [])
            .filter(cat => cat.topicGroup === group)
            .map(cat => cat.id);

        if (groupIds.length === 0) return;

        setSelectedCategoryIds(prevState => {
            const allSelected = groupIds.every(id => prevState.includes(id));
            const newIds = allSelected
                ? prevState.filter(id => !groupIds.includes(id))
                : [...prevState, ...groupIds];
            return [...new Set(newIds)];
        });
    };

    useEffect(() => {
        if (categoryOptions) {
            const queryParams = new URLSearchParams(location.search);
            const categoryIds = queryParams.getAll(CATEGORY_ID);
            setSelectedCategoryIds(excludeNotInObjectIds(categoryIds, availableCategories));
        }
    }, [categoryOptions]);

    useEffect(() => {
        if (!location.search || !categoryOptions) return;
        const queryParams = new URLSearchParams(location.search);
        const categoryIds = queryParams.getAll(CATEGORY_ID);
        const filterCategoryIds = excludeNotInObjectIds(categoryIds, availableCategories);
        const isSearchParam = queryParams.get('search');
        if (!isSearchParam) {
            setPrevQuery(queryParams.toString());
        } else if (isSearchParam && !isSearch) {
            setIsSearched(isSearchParam);
        }
        setSelectedCategoryIds(filterCategoryIds);
    }, [location.search]);

    useEffect(() => {
        if (!printTitle && sports.length > 0) {
            setPrintTitle(getPrintTitle());
        }
    }, [sports]);

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const isPrint = queryParams.get('print');
        const sportID = queryParams.get('sportId');
        if (isPrint && setSelectedColumn) {
            setSelectedColumn(isPrint);
            setIsPrintUrl(sportID);
        }
    }, []);

    useEffect(() => {
        setIsLoading(true);
        const queryParams = new URLSearchParams(location.search);
        const sportID = queryParams.get('sportId');
        if (isSearch) {
            const newQueryParams = new URLSearchParams();
            if (queryParams.get('locale')) newQueryParams.append('locale', queryParams.get('locale'));
            newQueryParams.append('search', isSearch);
            newQueryParams.append('sportId', sportID);
            history.replace(resolveRoute(routePaths.CATEGORIES, {}, {search: newQueryParams.toString()}));
        } else if (prevQuery) {
            setIsMenuOpen(false);
            history.replace(resolveRoute(routePaths.CATEGORIES, {}, {search: prevQuery}));
        } else {
            setPrevQuery(DEFAULT_CATS_URL + `&sportId=${sportID}`);
        }
    }, [isSearch]);

    useOutsideClick(dropDownRef, handleClickOutside);

    return (
        <div className={`${isPrintUrl ? 'sski-c-top-nav__print_active' : ''}`}>
            {(!isPrintUrl) ? (
                <div
                    className={`is-position-absolute ${isMenuOpen ? 'sski-c-top-navigation-bar__navigation-line' : ''}`}
                >
                    <button
                        type="button"
                        className="sski-c-top-navigation-bar__back_button button is-white is-rounded is-responsive is-size-6 has-text-weight-normal"
                        onClick={handleClickBack}
                    >
                        <img
                            alt="arrow-left"
                            className={`${!isMobile ? 'mr-2' : ''}`}
                            src={`${process.env.PUBLIC_URL}/assets/icons/arrow-left.svg`}
                        />
                        {!isMobile ? translateScreen('button_label.back') : null}
                    </button>
                    <div
                        className={`sski-c-top-navigation-bar is-flex is-justify-content-center ${isMenuOpen
                            ? 'sski-c-top-navigation-bar__background-overlay'
                            : ''
                        } ${isMobile ? 'px-2 pb-2' : ''}`}
                    >
                        <div ref={dropDownRef} className={`mt-2 dropdown ${isMenuOpen ? 'is-active' : ''}`}>
                            <div className="dropdown-trigger">
                                <button
                                    type="button"
                                    className={`sski-c-top-navigation-bar__dropdown_button button is-rounded
                                                ${isMobile ? 'is-size-6 px-2 py-1' : 'is-size-4'} has-text-weight-medium
                                     `}
                                    aria-haspopup="true"
                                    aria-controls="dropdown-menu"
                                    onClick={() => setIsMenuOpen(!isMenuOpen)}
                                >
                                    <img
                                        alt="Search icon"
                                        src={`${process.env.PUBLIC_URL}/assets/icons/search.svg`}
                                        className="mr-3"
                                    />
                                    <span className="mr-3">
                                        {isSearch || translateScreen('button_label.topics', {
                                            chosenTopics: availableCategories.length > 0
                                                ? selectedCategoryIds.filter(id => (
                                                    id !== topicTypes.AGE_CATEGORY.toLowerCase()
                                                )).length
                                                : 0,
                                            totalTopics: availableCategories.find(option => option.id === topicTypes.AGE_CATEGORY.toLowerCase()) ? availableCategories.length - 1 : availableCategories.length,
                                        })}
                                    </span>
                                    {isSearch
                                        ? (
                                            <img
                                                alt="close search icon"
                                                src={`${process.env.PUBLIC_URL}/assets/icons/close_search.svg`}
                                                onClick={e => { e.stopPropagation(); setIsSearched(CLEAR_SEARCH); setSearchedTerm(''); }}
                                            />
                                        )
                                        : (
                                            <img
                                                alt="drop-down-arrow"
                                                src={`${process.env.PUBLIC_URL}/assets/icons/arrow-down-bold.svg`}
                                            />
                                        )}
                                </button>
                            </div>
                            <div
                                className={`sski-c-top-navigation-bar__dropdown-menu dropdown-menu
                                            ${searchedTerm ? 'search-active' : ''}
                                `}
                                id="dropdown-menu"
                                role="menu"
                            >
                                <div className="sski-c-top-navigation-bar__dropdown-content-frame">
                                    <div className="sski-c-top-navigation-bar__dropdown-content dropdown-content p-4">
                                        <div
                                            className="sski-c-top-navigation-bar__button-container_first is-flex is-justify-content-start"
                                        >
                                            <SearchNavigation
                                                handleSearch={handleSearch}
                                                searchTerm={searchedTerm}
                                                setSearchTerm={setSearchedTerm}
                                                closeDropDown={setIsMenuOpen}
                                            />
                                        </div>
                                        <div className="sski-control">
                                            <div className="sski-c-top-navigation-bar_container_content">
                                                {!searchedTerm && (
                                                <div
                                                    className="sski-c-top-navigation-bar__button-container is-flex is-justify-content-end"
                                                >
                                                    <button
                                                        className="pt-4 p-2"
                                                        onClick={resetAll}
                                                    >{translateScreen('button_label.reset_all')}
                                                    </button>
                                                </div>
                                                )}
                                                {groupedCategories && Object.keys(groupedCategories).map((key, index) => (
                                                    <div
                                                        key={key}
                                                        className={`p-2 mt-6 ${index === Object.keys(groupedCategories).length - 1
                                                            ? 'sski-c-top-navigation-bar-bar__category--margin-bottom'
                                                            : 'mb-6'}`}
                                                    >
                                                        <p className="sski-c-top-navigation-bar__category-title" onClick={() => handleGroup(key.toLowerCase())}>
                                                            {translateScreen(`category_type.${key.toLowerCase()}`)}
                                                        </p>
                                                        {groupedCategories[key]
                                                            && Object.values(groupedCategories[key]).map(category => (
                                                                <div
                                                                    key={category.id}
                                                                    className="sski-c-top-navigation-bar__dropdown-item is-flex is-justify-content-space-between is-align-items-center"
                                                                    onClick={() => setSelectedCategoryIds(prevState => {
                                                                        return prevState.includes(category.id)
                                                                            ? (prevState.filter(id => category.id !== id))
                                                                            : [...prevState, category.id];
                                                                    })}
                                                                >
                                                                    <p>
                                                                        {category.name}
                                                                    </p>
                                                                    {selectedCategoryIds.includes(category.id) && (
                                                                        <img
                                                                            alt="checkmark"
                                                                            src={`${process.env.PUBLIC_URL}/assets/icons/checkmark.svg`}
                                                                        />
                                                                    )}
                                                                </div>
                                                            ))}
                                                    </div>
                                                ))}
                                            </div>
                                            {!searchedTerm?.length
                                                && (
                                                    <div
                                                        className="sski-c-top-navigation-bar__dropdown-content-sticky-bottom p-5"
                                                    >

                                                        <button
                                                            type="button"
                                                            disabled={visibleCategories.length === 0}
                                                            className={`sski-c-top-navigation-bar__dropdown-content-button button is-rounded ${isMobile ? 'is-size-6' : 'is-size-4'}`}
                                                            onClick={handleSubmit}
                                                        >
                                                            {translateScreen('button_label.submit')}
                                                        </button>

                                                    </div>
                                                )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )
                : (isPrintUrl && sports.length == 0) ? (
                    <div className="loader-container mt-4">
                        <div className="a4-page">
                            {[...Array(6)].map((_, i) => <PhaseLoader full={true} />)}
                        </div>
                    </div>
                )
                    : null}
            {(sports.length > 0)
                && <PrintData isShown={selectedColumn} printTitle={printTitle} isPrintUrl={isPrintUrl} selectedColumn={selectedColumn} />}
        </div>
    );
};

TopNavigationBar.propTypes = {
    history: PropTypes.object.isRequired,
    // location: PropTypes.object.isRequired, todo investigate why location object isn't updated with search params
    categoryOptions: PropTypes.array,
    setIsLoading: PropTypes.func,
};

TopNavigationBar.defaultProps = {
    categoryOptions: [],
};

const mapStateToProps = state => {
    return {
        categoryOptions: state.categories.categoryOptions,
        sports: state.dashboard.sports,
        sportId: state.application.sportId,
    };
};

const mapDispatchToProps = dispatch => {
    return {
        resetCategories: payload => dispatch({
            type: categoryActionTypes.RESET_CATEGORY_ELEMENTS,
            payload,
        }),
    };
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(TopNavigationBar));
